<script>
import axios from '@/utils/axios';

export default {
  inject: ['setLoading', 'auth'],
  props: {
    modules: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      errors: {},
      id: null,
      name: '',
      permissions: {},
      salesOrderRole: null,
      is_hr: false,
      _hrTouched: false, // tracks if user manually changed is_hr
    };
  },
  computed: {
    hasSalesOrderModuleAccess(){
     const salesOrderModule = this.modules.find(m => m.name === 'Sales_Order');
     if (!salesOrderModule) return false;
     return this.hasPermission(salesOrderModule.id, 'read');
    },

    isSalesOrderReadEnabled(){
     const salesOrderModule = this.modules.find(m => m.name === 'Sales_Order');
     if (!salesOrderModule || !this.permissions[salesOrderModule.id]) return false;
     return this.permissions[salesOrderModule.id].read;
    },

    salesOrderModuleId() {
     return this.modules.find(m => m.name === 'Sales_Order')?.id;
    },

    // ── HR ──
    hrmsSetupModuleId() {
     return this.modules.find(m => m.name === 'HRMS_Setup')?.id;
    },
    isHrmsSetupReadEnabled() {
     const id = this.hrmsSetupModuleId;
     if (!id || !this.permissions[id]) return false;
     return this.permissions[id].read;
    },
  },
  watch: {
   'permissions': {
    handler(newPermissions) {
     // Sales Order — read off clears role
     const salesOrderId = this.salesOrderModuleId;
     if(salesOrderId && newPermissions[salesOrderId] && !newPermissions[salesOrderId].read) {
       this.salesOrderRole = null;
     }

     // HR — HRMS_Setup read on → auto-check (unless user manually changed); off → uncheck
     const hrmsId = this.hrmsSetupModuleId;
     if (hrmsId && newPermissions[hrmsId]) {
       if (newPermissions[hrmsId].read) {
         if (!this._hrTouched) this.is_hr = true;
       } else {
         this.is_hr = false;
         this._hrTouched = false;
       }
     }
    },
    deep: true
   }
  },

  methods: {
   hasPermission(moduleId, operation) {
    if (!this.auth || this.auth.type === 'SUPER_ADMIN') return true;
    const perms = this.auth.role?.permissions || [];
    const modulePerm = perms.find(p => p.module_id === moduleId);
    return modulePerm ? modulePerm[operation] : false;
   },

   onSalesOrderReadChange(event) {
    if (!event.target.checked && this.salesOrderModuleId) {
     this.salesOrderRole = null;
    }
   },

   // user manually toggled HR card
   onHrToggle() {
    this._hrTouched = true;
   },

   initPermissions() {
    this.permissions = {};
    this.modules.forEach(module => {
     this.permissions[module.id] = {
      create: false,
      read: false,
      update: false,
      delete: false,
     };
    });
   },

   formatPermissions() {
    return Object.entries(this.permissions).map(([module_id, perms]) => ({
      module_id: parseInt(module_id),
      ...perms,
    }));
   },

   getSalesOrderFlags() {
    return {
     is_sales_order_approver: this.salesOrderRole === 'approver',
     is_sales_order_production: this.salesOrderRole === 'production',
    };
   },

   add() {
    this.resetForm();
    this.initPermissions();
   },

   editRole(role) {
    this.resetForm();
    this.id = role.id;
    this.name = role.name;

     if(role.is_sales_order_approver){
      this.salesOrderRole = 'approver';
     }else if(role.is_sales_order_production){
      this.salesOrderRole = 'production';
     }

     this.is_hr = role.is_hr || false;
     this._hrTouched = true; // editing existing — respect saved value, don't auto-override

     this.initPermissions();

     if(Array.isArray(role.permissions)){
      role.permissions.forEach((p) => {
       if(this.permissions[p.module_id]){
        this.permissions[p.module_id].create = p.create;
        this.permissions[p.module_id].read = p.read;
        this.permissions[p.module_id].update = p.update;
        this.permissions[p.module_id].delete = p.delete;
       }
      });
     }
    },

    async addUpdateRole() {
     this.errors = {};
     this.setLoading(true);

     const payload = {
      name: this.name,
      permissions: this.formatPermissions(),
      is_hr: this.is_hr,
      ...this.getSalesOrderFlags(),
     };

     try {
      let response;
      if(this.id){
       response = await axios.put('/roles/update', {
        id: this.id,
        ...payload,
       });
      }else{
       response = await axios.post('/roles/create', payload);
      }

     this.$swalToast(response.data.message);
     this.$emit('updated');
     this.$closeModal('role_permission_modal');
     this.resetForm();
    }catch(error){
     if(error.type === 'validation'){
      this.errors = error.errors;
     }else{
      this.$swalToast(error.message, 'error');
     }
    }finally{
     this.setLoading(false);
    }
   },

   async deleteRole() {
    this.setLoading(true);
     try{
      const response = await axios.delete(`/roles/${this.id}/delete`);
      this.$swalToast(response.data.message);
      this.$emit('updated');
      this.$closeModal('delete_modal');
     }catch(error){
      this.$swalToast(error.message, 'error');
     }finally{
      this.setLoading(false);
     }
    },

    resetForm() {
     this.id = null;
     this.name = '';
     this.errors = {};
     this.permissions = {};
     this.salesOrderRole = null;
     this.is_hr = false;
     this._hrTouched = false;
    },
  },
};
</script>

<template>
  <!-- Role Add/Edit Modal -->
  <div class="modal fade" id="role_permission_modal">
    <div class="modal-dialog modal-dialog-centered modal-lg w-100">
      <div class="modal-content border-0 shadow-lg" style="border-radius: 16px; overflow: hidden;">

        <!-- Modal Header -->
        <div class="modal-header border-0 px-4 pt-4 pb-3" style="background: linear-gradient(135deg, #f8faff 0%, #eef2ff 100%);">
          <div class="d-flex align-items-center gap-3">
            <div class="d-flex align-items-center justify-content-center rounded-3"
              style="width: 40px; height: 40px; background: linear-gradient(135deg, #6366f1, #4f46e5); box-shadow: 0 4px 12px rgba(99,102,241,0.35);">
              <i class="ti ti-shield-lock text-white" style="font-size: 18px;"></i>
            </div>
            <div>
              <h5 class="modal-title mb-0 fw-semibold" style="color: #1e1b4b; font-size: 17px; letter-spacing: -0.2px;">
                {{ id ? 'Edit Role' : 'Create New Role' }}
              </h5>
              <p class="mb-0 text-muted" style="font-size: 12px;">
                {{ id ? 'Update role name and module permissions' : 'Define a new role with module permissions' }}
              </p>
            </div>
          </div>
          <button type="button" class="btn-close d-flex align-items-center justify-content-center rounded-2"
            data-bs-dismiss="modal" aria-label="Close"
            style="width: 32px; height: 32px; background: #fff; border: 1px solid #e5e7eb; opacity: 1; box-shadow: 0 1px 4px rgba(0,0,0,0.06);">
            <i class="ti ti-x" style="font-size: 14px; color: #6b7280;"></i>
          </button>
        </div>

        <form @submit.prevent="addUpdateRole">
          <div class="modal-body px-4 py-4">

            <!-- Role Name Field -->
            <div class="mb-4">
              <label class="form-label fw-medium mb-1" style="font-size: 13px; color: #374151; letter-spacing: 0.1px;">
                Role Name
              </label>
              <input
                type="text"
                class="form-control"
                v-model="name"
                placeholder="e.g. Sales Manager, Warehouse Staff"
                style="border-radius: 10px; border: 1.5px solid #e5e7eb; padding: 10px 14px; font-size: 14px; transition: border-color 0.2s, box-shadow 0.2s;"
                onfocus="this.style.borderColor='#6366f1'; this.style.boxShadow='0 0 0 3px rgba(99,102,241,0.12)'"
                onblur="this.style.borderColor='#e5e7eb'; this.style.boxShadow='none'"
              />
              <div v-if="errors.name" class="mt-1 d-flex align-items-center gap-1" style="color: #ef4444; font-size: 12px;">
                <i class="ti ti-alert-circle" style="font-size: 13px;"></i>
                {{ errors.name[0] }}
              </div>
            </div>

            <!-- Sales Order Role Section -->
            <div v-if="hasSalesOrderModuleAccess && isSalesOrderReadEnabled" class="mb-4">
              <label class="form-label fw-medium mb-2" style="font-size: 13px; color: #374151; letter-spacing: 0.1px;">
                Sales Order Role
              </label>
              <div class="d-flex gap-3">

                <!-- Approver Card -->
                <label for="approver"
                  class="flex-fill d-flex align-items-center gap-3 px-4 py-3 rounded-3 cursor-pointer"
                  :style="{
                    border: salesOrderRole === 'approver' ? '2px solid #6366f1' : '2px solid #e5e7eb',
                    background: salesOrderRole === 'approver' ? 'linear-gradient(135deg, #eef2ff, #f5f3ff)' : '#fafafa',
                    transition: 'all 0.2s',
                    cursor: 'pointer'
                  }">
                  <input class="form-check-input mt-0 flex-shrink-0" type="radio" id="approver" value="approver" v-model="salesOrderRole" style="accent-color: #6366f1;" />
                  <div>
                    <div class="fw-semibold" style="font-size: 13px; color: #1e1b4b;">Sales Order Approver</div>
                    <div class="text-muted" style="font-size: 11px;">Can review and approve orders</div>
                  </div>
                </label>

                <!-- Production Card -->
                <label for="production"
                  class="flex-fill d-flex align-items-center gap-3 px-4 py-3 rounded-3 cursor-pointer"
                  :style="{
                    border: salesOrderRole === 'production' ? '2px solid #6366f1' : '2px solid #e5e7eb',
                    background: salesOrderRole === 'production' ? 'linear-gradient(135deg, #eef2ff, #f5f3ff)' : '#fafafa',
                    transition: 'all 0.2s',
                    cursor: 'pointer'
                  }">
                  <input class="form-check-input mt-0 flex-shrink-0" type="radio" id="production" value="production" v-model="salesOrderRole" style="accent-color: #6366f1;" />
                  <div>
                    <div class="fw-semibold" style="font-size: 13px; color: #1e1b4b;">Sales Order Production</div>
                    <div class="text-muted" style="font-size: 11px;">Can manage production flow</div>
                  </div>
                </label>

              </div>
            </div>

            <!-- HR Role Section (shows when HRMS_Setup read is on) -->
            <div v-if="isHrmsSetupReadEnabled" class="mb-4">
              <label class="form-label fw-medium mb-2" style="font-size: 13px; color: #374151; letter-spacing: 0.1px;">
                HR Access
              </label>
              <label for="is_hr"
                class="d-flex align-items-center gap-3 px-4 py-3 rounded-3 cursor-pointer"
                :style="{
                  border: is_hr ? '2px solid #6366f1' : '2px solid #e5e7eb',
                  background: is_hr ? 'linear-gradient(135deg, #eef2ff, #f5f3ff)' : '#fafafa',
                  transition: 'all 0.2s',
                  cursor: 'pointer'
                }">
                <input class="form-check-input mt-0 flex-shrink-0" type="checkbox" id="is_hr" v-model="is_hr" @change="onHrToggle" style="accent-color: #6366f1; width:18px; height:18px;" />
                <div>
                  <div class="fw-semibold" style="font-size: 13px; color: #1e1b4b;">HR Role</div>
                  <div class="text-muted" style="font-size: 11px;">Gets admin-level access to all employees' Leave, Attendance & Payroll</div>
                </div>
              </label>
            </div>

            <!-- Permissions Table -->
            <div v-if="modules.length">
              <div class="d-flex align-items-center justify-content-between mb-2">
                <label class="form-label fw-medium mb-0" style="font-size: 13px; color: #374151; letter-spacing: 0.1px;">
                  Module Permissions
                </label>
                <span class="badge" style="background: #eef2ff; color: #6366f1; font-size: 11px; font-weight: 500; border-radius: 20px; padding: 4px 10px;">
                  {{ modules.length }} modules
                </span>
              </div>

              <div class="rounded-3 overflow-hidden" style="border: 1.5px solid #e5e7eb;">
                <table class="table mb-0" style="font-size: 13.5px;">
                  <thead>
                    <tr style="background: linear-gradient(135deg, #f8faff, #eef2ff);">
                      <th class="px-3 py-3 fw-semibold" style="color: #fff; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; width: 40px;">#</th>
                      <th class="px-3 py-3 fw-semibold" style="color: #fff; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px;">Module</th>
                      <th class="px-3 py-3 fw-semibold text-center" style="color: #fff; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px;">Create</th>
                      <th class="px-3 py-3 fw-semibold text-center" style="color: #fff; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px;">Read</th>
                      <th class="px-3 py-3 fw-semibold text-center" style="color: #fff; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px;">Update</th>
                      <th class="px-3 py-3 fw-semibold text-center" style="color: #fff; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px;">Delete</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(module, index) in modules"
                      :key="module.id"
                      :style="{
                        background: index % 2 === 0 ? '#ffffff' : '#fafbff',
                        borderTop: '1px solid #f0f0f0',
                        transition: 'background 0.15s'
                      }"
                    >
                      <td class="px-3 py-3 text-muted" style="font-size: 12px; color: #9ca3af !important;">{{ index + 1 }}</td>
                      <td class="px-3 py-3">
                        <span class="fw-medium" style="color: #1f2937;">{{ module.name.replace(/_/g, ' ') }}</span>
                      </td>
                      <td class="px-3 py-3 text-center">
                        <div class="d-flex justify-content-center">
                          <input
                            type="checkbox"
                            :checked="permissions[module.id]?.create || false"
                            @change="permissions[module.id].create = $event.target.checked"
                            :disabled="!hasPermission(module.id, 'create')"
                            style="width: 16px; height: 16px; accent-color: #6366f1; cursor: pointer; border-radius: 4px;"
                          />
                        </div>
                      </td>
                      <td class="px-3 py-3 text-center">
                        <div class="d-flex justify-content-center">
                          <input
                            type="checkbox"
                            :checked="permissions[module.id]?.read || false"
                            @change="permissions[module.id].read = $event.target.checked"
                            :disabled="!hasPermission(module.id, 'read')"
                            style="width: 16px; height: 16px; accent-color: #6366f1; cursor: pointer; border-radius: 4px;"
                          />
                        </div>
                      </td>
                      <td class="px-3 py-3 text-center">
                        <div class="d-flex justify-content-center">
                          <input
                            type="checkbox"
                            :checked="permissions[module.id]?.update || false"
                            @change="permissions[module.id].update = $event.target.checked"
                            :disabled="!hasPermission(module.id, 'update')"
                            style="width: 16px; height: 16px; accent-color: #6366f1; cursor: pointer; border-radius: 4px;"
                          />
                        </div>
                      </td>
                      <td class="px-3 py-3 text-center">
                        <div class="d-flex justify-content-center">
                          <input
                            type="checkbox"
                            :checked="permissions[module.id]?.delete || false"
                            @change="permissions[module.id].delete = $event.target.checked"
                            :disabled="!hasPermission(module.id, 'delete')"
                            style="width: 16px; height: 16px; accent-color: #ef4444; cursor: pointer; border-radius: 4px;"
                          />
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

          </div>

          <!-- Modal Footer -->
          <div class="modal-footer border-0 px-4 pb-4 pt-2 gap-2">
            <button
              type="button"
              class="btn px-4 py-2 fw-medium"
              data-bs-dismiss="modal"
              style="border-radius: 10px; border: 1.5px solid #e5e7eb; background: #fff; color: #6b7280; font-size: 13.5px; transition: all 0.2s;"
              onmouseover="this.style.background='#f9fafb'; this.style.borderColor='#d1d5db';"
              onmouseout="this.style.background='#fff'; this.style.borderColor='#e5e7eb';"
            >
              Cancel
            </button>
            <button
              type="submit"
              class="btn px-4 py-2 fw-medium text-white"
              style="border-radius: 10px; background: linear-gradient(135deg, #6366f1, #4f46e5); border: none; font-size: 13.5px; box-shadow: 0 4px 12px rgba(99,102,241,0.3); transition: all 0.2s;"
              onmouseover="this.style.boxShadow='0 6px 16px rgba(99,102,241,0.45)'; this.style.transform='translateY(-1px)';"
              onmouseout="this.style.boxShadow='0 4px 12px rgba(99,102,241,0.3)'; this.style.transform='translateY(0)';"
            >
              <i class="ti ti-device-floppy me-1" style="font-size: 14px;"></i>
              {{ id ? 'Update Role' : 'Save Role' }}
            </button>
          </div>
        </form>

      </div>
    </div>
  </div>

  <!-- Delete Modal -->
  <div class="modal fade" id="delete_modal">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content border-0 shadow-lg text-center" style="border-radius: 16px; overflow: hidden;">
        <div class="modal-body px-4 pt-5 pb-4">
          <div class="d-flex align-items-center justify-content-center mx-auto mb-4 rounded-3"
            style="width: 64px; height: 64px; background: linear-gradient(135deg, #fff1f2, #ffe4e6); box-shadow: 0 4px 16px rgba(239,68,68,0.2);">
            <i class="ti ti-trash-x" style="font-size: 28px; color: #ef4444;"></i>
          </div>
          <h5 class="fw-semibold mb-1" style="color: #1f2937; font-size: 17px;">Delete Role?</h5>
          <p class="text-muted mb-4" style="font-size: 13.5px; max-width: 260px; margin: 0 auto 24px;">
            This action is permanent and cannot be undone. The role will be removed immediately.
          </p>
          <div class="d-flex justify-content-center gap-2">
            <a
              href="javascript:void(0);"
              class="btn px-4 py-2 fw-medium"
              data-bs-dismiss="modal"
              style="border-radius: 10px; border: 1.5px solid #e5e7eb; background: #fff; color: #6b7280; font-size: 13.5px;"
            >
              Cancel
            </a>
            <a
              href="javascript:void(0);"
              class="btn px-4 py-2 fw-medium text-white"
              @click="deleteRole"
              style="border-radius: 10px; background: linear-gradient(135deg, #f87171, #ef4444); border: none; font-size: 13.5px; box-shadow: 0 4px 12px rgba(239,68,68,0.3);"
            >
              <i class="ti ti-trash me-1" style="font-size: 14px;"></i>
              Yes, Delete
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>